import type { Certificate } from "../../shared/api-types/certificate";

import { formatDate } from "../../services/formatters";
import type { CourseCertificate, CertificateMap } from "../../api/compliance";

export type HistoryItem = Certificate & {
  name: string;
  userId: number;
  isCustom: boolean;
};

const getDisplayObject = (
  userId: number,
  courseName: string,
  certificate: Certificate,
  isCustom: boolean = false,
): HistoryItem => ({
  ...certificate,
  name: courseName,
  userId,
  isCustom,
  uploadedAt: certificate.uploadedAt ? formatDate(certificate.uploadedAt) : "",
  approvedAt: certificate.approvedAt ? formatDate(certificate.approvedAt) : "",
});

const HistoryService = {
  getHistory(
    courseCertificate: CourseCertificate | null | undefined,
    certificateMap: CertificateMap,
    userId: number,
  ): HistoryItem[] {
    const items: HistoryItem[] = [];

    if (!courseCertificate) {
      return items;
    }

    if (courseCertificate.custom?.history) {
      for (const cert of courseCertificate.custom.history) {
        // Only include certificates with valid completion dates
        if (cert.completedDate) {
          items.push(getDisplayObject(userId, courseCertificate.name, cert, true));
        }
      }
    } else if (courseCertificate.group?.courses) {
      for (const courseId of courseCertificate.group.courses) {
        const groupCourseCert = certificateMap[courseId];
        // Only include certificates with valid completion dates and matching id because of cert inheritance (inherited cert has not same id as course)
        if (groupCourseCert?.certificate?.completedDate && groupCourseCert.id === groupCourseCert.certificate.id) {
          items.push(getDisplayObject(userId, groupCourseCert.name, groupCourseCert.certificate, false));
        }
      }
    } else {
      // Only include main certificate if it has a valid completion date
      if (courseCertificate.certificate?.completedDate) {
        items.push(getDisplayObject(userId, courseCertificate.name, courseCertificate.certificate, false));
      }

      if (courseCertificate.refreshers && courseCertificate.refreshers.length > 0) {
        for (const refresher of courseCertificate.refreshers) {
          // Only include refreshers with valid completion dates
          if (refresher?.certificate?.completedDate) {
            items.push(getDisplayObject(userId, refresher.name, refresher.certificate, false));
          }
        }
      }
    }

    items.sort((a, b) => {
      if (!a.completedDate) return 1;
      if (!b.completedDate) return -1;
      return new Date(b.completedDate).getTime() - new Date(a.completedDate).getTime();
    });

    return items;
  },
};

export default HistoryService;
